import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { env } from '../../config/env';
import { ErrorHandler } from './errorHandler';
import { AuthManager } from './authManager';

export class HttpClient {
  private client: AxiosInstance;
  private errorHandler: ErrorHandler;
  private authManager: AuthManager;

  constructor() {
    this.errorHandler = new ErrorHandler();
    
    // Create Axios instance with default configuration
    this.client = axios.create({
      baseURL: env.API_BASE_URL,
      timeout: env.API_TIMEOUT || 180000, // Increased to 3 minutes for AI operations
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Initialize auth manager
    this.authManager = new AuthManager(this.client, this.errorHandler);

    // Setup interceptors
    this.setupRequestInterceptors();
    this.setupResponseInterceptors();
  }

  /**
   * Get the configured Axios instance
   */
  getInstance(): AxiosInstance {
    return this.client;
  }

  /**
   * Get the error handler instance
   */
  getErrorHandler(): ErrorHandler {
    return this.errorHandler;
  }

  /**
   * Get the auth manager instance
   */
  getAuthManager(): AuthManager {
    return this.authManager;
  }

  /**
   * Setup request interceptors
   */
  private setupRequestInterceptors(): void {
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Check if data is FormData - if so, don't transform and remove Content-Type
        const isFormData = config.data instanceof FormData;
        
        // Transform request data (skip for FormData)
        if (config.data && !isFormData) {
          config.data = this.transformRequestData(config.data);
        }

        // Add authentication if required
        if (this.authManager.requiresAuth(config)) {
          config = this.authManager.attachToken(config) as InternalAxiosRequestConfig;
        }

        // Add request ID for tracking
        if (!config.headers) {
          config.headers = {} as any;
        }
        config.headers['X-Request-ID'] = this.generateRequestId();
        
        // For FormData, remove Content-Type to let browser set it with boundary
        if (isFormData && config.headers['Content-Type']) {
          delete config.headers['Content-Type'];
        }

        // Log request in development
        if (import.meta.env?.DEV) {
          console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, {
            data: isFormData ? 'FormData' : config.data,
            params: config.params,
          });
        }

        return config;
      },
      (error) => {
        this.errorHandler.logError(
          this.errorHandler.handleError(error),
          'Request interceptor error'
        );
        return Promise.reject(this.errorHandler.handleError(error));
      }
    );
  }

  /**
   * Setup response interceptors
   */
  private setupResponseInterceptors(): void {
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Transform response data
        if (response.data) {
          response.data = this.transformResponseData(response.data);
        }

        // Log response in development
        if (import.meta.env?.DEV) {
          console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            data: response.data,
          });
        }

        return response;
      },
      async (error) => {
        // Log error in development
        if (import.meta.env?.DEV) {
          console.error(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
            status: error.response?.status,
            data: error.response?.data,
          });
        }

        // Handle authentication failures
        if (error.response?.status === 401) {
          try {
            return await this.authManager.handleAuthFailure(error, error.config);
          } catch (authError) {
            return Promise.reject(authError);
          }
        }

        // Handle other errors
        const apiError = this.errorHandler.handleError(error);
        this.errorHandler.logError(apiError, 'Response interceptor error');
        
        return Promise.reject(apiError);
      }
    );
  }

  /**
   * Transform request data before sending
   */
  private transformRequestData(data: any): any {
    if (!data) return data;

    // Handle FormData (don't transform)
    if (data instanceof FormData) {
      return data;
    }

    // Handle Date objects and nested objects
    return this.deepTransformDates(data, 'request');
  }

  /**
   * Transform response data after receiving
   */
  private transformResponseData(data: any): any {
    if (!data) return data;

    // Transform ISO date strings back to Date objects
    return this.deepTransformDates(data, 'response');
  }

  /**
   * Deep transform dates in objects
   */
  private deepTransformDates(obj: any, direction: 'request' | 'response'): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.deepTransformDates(item, direction));
    }

    if (obj instanceof Date) {
      return direction === 'request' ? obj.toISOString() : obj;
    }

    if (typeof obj === 'string' && direction === 'response') {
      // Check if string is an ISO date
      if (this.isISODateString(obj)) {
        return new Date(obj);
      }
      return obj;
    }

    if (typeof obj === 'object' && obj.constructor === Object) {
      const transformed: any = {};
      
      Object.keys(obj).forEach(key => {
        transformed[key] = this.deepTransformDates(obj[key], direction);
      });

      return transformed;
    }

    return obj;
  }

  /**
   * Check if string is an ISO date string
   */
  private isISODateString(value: string): boolean {
    // Match ISO 8601 date strings
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
    return isoDateRegex.test(value) && !isNaN(Date.parse(value));
  }

  /**
   * Generate unique request ID for tracking
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Update client configuration
   */
  updateConfig(config: Partial<AxiosRequestConfig>): void {
    Object.assign(this.client.defaults, config);
  }

  /**
   * Set base URL
   */
  setBaseURL(baseURL: string): void {
    this.client.defaults.baseURL = baseURL;
  }

  /**
   * Set timeout
   */
  setTimeout(timeout: number): void {
    this.client.defaults.timeout = timeout;
  }

  /**
   * Add custom header
   */
  setHeader(name: string, value: string): void {
    this.client.defaults.headers.common[name] = value;
  }

  /**
   * Remove custom header
   */
  removeHeader(name: string): void {
    delete this.client.defaults.headers.common[name];
  }
}